import sessionStore from 'client/Session/SessionStore';

export const TEACHER_RESTRICTION_MESSAGE = 'Access restricted by your admin';

export const RESTRICTED_QUESTION_LABEL = 'Restricted Question';

interface ContentLockable {
  isContentLocked?: () => boolean;
}

// Admins bypass enforcement, so they're excluded even when they also hold a teacher role.
export function isTeacherRestrictionViewer(): boolean {
  return sessionStore.isTeacher() && !sessionStore.isSchoolAdministrator();
}

export function isQuestionRestrictedForTeacher(
  question: ContentLockable | null | undefined
): boolean {
  return isTeacherRestrictionViewer() && Boolean(question?.isContentLocked?.());
}

interface RestrictableGuideLevel {
  getId: () => string;
  getParentId?: () => string | null | undefined;
  isContentLocked?: () => boolean;
}

/**
 * Guide-level ids restricted for the teacher, rolling child locks upward: a level is restricted when
 * it's directly content-locked, OR it has children and every one of them is restricted — so a quiz
 * whose question types are all locked shows the indicator even though the quiz carries no direct lock
 * (`is_content_locked` is per-level and doesn't aggregate from descendants). Returns an empty set for
 * non-teacher-restriction viewers, so callers can use it unconditionally. Pass the question-bearing
 * levels of the surface so structural/empty levels don't gate roll-up.
 */
export function getTeacherRestrictedGuideLevelIds(
  guideLevels: RestrictableGuideLevel[]
): Set<string> {
  if (!isTeacherRestrictionViewer()) {
    return new Set();
  }
  const childrenByParentId = new Map<string, RestrictableGuideLevel[]>();
  guideLevels.forEach((level) => {
    const parentId = level.getParentId?.();
    if (parentId) {
      const siblings = childrenByParentId.get(parentId);
      if (siblings) {
        siblings.push(level);
      } else {
        childrenByParentId.set(parentId, [level]);
      }
    }
  });
  const restrictedById = new Map<string, boolean>();
  const isRestricted = (level: RestrictableGuideLevel): boolean => {
    const id = level.getId();
    const memoized = restrictedById.get(id);
    if (memoized !== undefined) {
      return memoized;
    }
    restrictedById.set(id, false); // break cycles while recursing
    const children = childrenByParentId.get(id) ?? [];
    const restricted =
      Boolean(level.isContentLocked?.()) || (children.length > 0 && children.every(isRestricted));
    restrictedById.set(id, restricted);
    return restricted;
  };
  const restrictedIds = new Set<string>();
  guideLevels.forEach((level) => {
    if (isRestricted(level)) {
      restrictedIds.add(level.getId());
    }
  });
  return restrictedIds;
}

/**
 * Guide-level ids that CONTAIN restricted content: a level is included when any of its descendants is
 * content-locked (any-descendant roll-up, distinct from the all-children roll-up above). Used to flag
 * a parent theme that holds locked guide levels (DQA #17841). Empty for non-teacher-restriction
 * viewers, so callers can use it unconditionally.
 */
export function getGuideLevelIdsWithLockedDescendant(
  guideLevels: RestrictableGuideLevel[]
): Set<string> {
  if (!isTeacherRestrictionViewer()) {
    return new Set();
  }
  const parentById = new Map<string, string | null | undefined>();
  guideLevels.forEach((level) => {
    parentById.set(level.getId(), level.getParentId?.());
  });
  const containsLocked = new Set<string>();
  guideLevels.forEach((level) => {
    if (!level.isContentLocked?.()) {
      return;
    }
    // Mark every ancestor of a locked level as containing locked content.
    let parentId = parentById.get(level.getId());
    const seen = new Set<string>(); // guard against cycles
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      containsLocked.add(parentId);
      parentId = parentById.get(parentId);
    }
  });
  return containsLocked;
}

interface QuestionSetLike {
  getQuestions?: () => {toArray?: () => ContentLockable[]} | undefined;
}

export function isQuestionSetRestrictedForTeacher(
  questionSet: QuestionSetLike | null | undefined
): boolean {
  if (!isTeacherRestrictionViewer()) {
    return false;
  }
  const questions = questionSet?.getQuestions?.()?.toArray?.() ?? [];
  return questions.some((question) => Boolean(question?.isContentLocked?.()));
}

/**
 * Reports variant: the backend exposes `isContentLocked` on the analytics row (`ReportResult`), not
 * on the nested question, and redacts the title server-side. Param is `unknown` to read defensively.
 */
export function isReportRowRestrictedForTeacher(row: unknown): boolean {
  if (!isTeacherRestrictionViewer()) {
    return false;
  }
  const reportRow = row as {isContentLocked?: boolean | null} | null | undefined;
  return Boolean(reportRow?.isContentLocked);
}

export const REPORTS_QUESTION_TITLE_SORT_KEY = 'questions.title';

/**
 * Title-sort only: sink title-hidden restricted questions to the end (sorting them by a
 * hidden title is meaningless). Server-side sorted + paginated, so this only reorders the
 * LOADED page; removable once the backend can sort isContentLocked last across all pages.
 */
export function partitionReportRowsRestrictedLast<T extends {isContentLocked?: boolean | null}>(
  rows: T[],
  {sortBy}: {sortBy: string | null}
): T[] {
  if (sortBy !== REPORTS_QUESTION_TITLE_SORT_KEY) {
    return rows;
  }
  const visible: T[] = [];
  const restricted: T[] = [];
  rows.forEach((row) => {
    if (row && isReportRowRestrictedForTeacher(row)) {
      restricted.push(row);
    } else {
      visible.push(row);
    }
  });
  return restricted.length > 0 ? [...visible, ...restricted] : rows;
}
